import { useCallback, useEffect } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useHistory, useParams } from 'react-router-dom'
import { useMediaQuery } from '@react-hookz/web'
import { A11y, Navigation, Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

import { GLOBAL_ROUTE_PATHS } from 'Router/Router.constants'

import { ImpactTracker } from 'Components/ImpactTracker/ImpactTracker'
import { PageHeader } from 'Components/PageHeader/PageHeader'

import { useStore } from 'Stores/index'

import {
  PORTAL_BREAKPOINTS,
  SLIDER_BREAKPOINTS_ADMIN_DASHBOARD,
} from 'Constants/constants'
import { PortalSectionEnum } from 'Constants/portalSection'

import { composePath } from 'Portal/src/utilities/composePath/composePath.util'

import { DashboardAccountManagerCard } from '../DashboardAccountManagerCard/DashboardAccountManagerCard'
import { DashboardBanner } from '../DashboardBanner/DashboardBanner'
import { DashboardDeviceCountCard } from '../DashboardDeviceCountCard/DashboardDeviceCountCard'
import { DashboardEarlyUpgradeEligibleCard } from '../DashboardEarlyUpgradeEligibleCard/DashboardEarlyUpgradeEligibleCard'
import { DashboardOrdersReportCard } from '../DashboardOrdersReportCard/DashboardOrdersReportCard'
import { DashboardStatisticCard } from '../DashboardStatisticCard/DashboardStatisticCard'
import { ShippedOrdersCard } from '../ShippedOrdersCard/ShippedOrdersCard'
import { SHIPPED_ORDERS_MOCK } from '../ShippedOrdersCard/ShippedOrdersCard.mock'
import { UnreturnedDeviesCard } from '../UnreturnedDevicesCard/UnreturnedDevicesCard'
import { UNRETURNED_DEVICES_MOCK } from '../UnreturnedDevicesCard/UnreturnedDevicesCard.mock'
import { organisationDashboardMockStatisticData } from './OrganisationDashboardDefault.mocks'

export const OrganisationDashboardDefault = () => {
  const { portalConfigStore, portalSettingsStore } = useStore()
  const { portalConfig } = portalConfigStore
  const { setCurrentPortalSection } = portalSettingsStore

  const history = useHistory()
  const { organisationId } = useParams<{ organisationId: string }>()

  useEffect(() => {
    setCurrentPortalSection(PortalSectionEnum.Dashboard)
  }, [setCurrentPortalSection])

  const isNewOrderButtonShown =
    portalConfig?.showNewDeviceButtonToAdmin ?? false

  const handleNewOrderButtonClick = useCallback(() => {
    history.push(
      composePath({
        params: { organisationId },
        path: GLOBAL_ROUTE_PATHS.organisation.orderNew,
      }),
    )
  }, [organisationId, history])

  const isHDDesktop = useMediaQuery(`(min-width: ${PORTAL_BREAKPOINTS.XXL})`)

  return (
    <Container className="OrganisationDashboard dashboard" fluid>
      <PageHeader portalSection={PortalSectionEnum.Dashboard} />

      <Row>
        <Col xs={12} xxl={8}>
          <DashboardBanner
            onNewOrderButtonClick={handleNewOrderButtonClick}
            showNewOrderButton={isNewOrderButtonShown}
            version="compact"
          />

          {!isHDDesktop && (
            <>
              <Swiper
                breakpoints={SLIDER_BREAKPOINTS_ADMIN_DASHBOARD}
                className="mb-5 mt-2 w-100"
                modules={[Navigation, Pagination, A11y]}
                navigation
                pagination={{ clickable: true }}
                spaceBetween={16}
              >
                {organisationDashboardMockStatisticData.map(data => (
                  <SwiperSlide key={data.contentType}>
                    <DashboardStatisticCard {...data} />
                  </SwiperSlide>
                ))}
              </Swiper>

              <Row>
                <Col md={6}>
                  <DashboardEarlyUpgradeEligibleCard />
                </Col>

                <Col md={6}>
                  <DashboardEarlyUpgradeEligibleCard />
                </Col>
              </Row>
            </>
          )}

          <Row>
            <Col md={6}>
              <ShippedOrdersCard orders={SHIPPED_ORDERS_MOCK} />
            </Col>

            <Col md={6}>
              <UnreturnedDeviesCard rentals={UNRETURNED_DEVICES_MOCK} />
            </Col>
          </Row>

          <DashboardDeviceCountCard />

          <DashboardOrdersReportCard />
        </Col>

        <Col xs={12} xxl={4}>
          {isHDDesktop && (
            <>
              <div className="OrganisationDashboard-statistic-card-container mb-4">
                {organisationDashboardMockStatisticData.map(data => (
                  <DashboardStatisticCard key={data.contentType} {...data} />
                ))}
              </div>

              <DashboardEarlyUpgradeEligibleCard />

              <DashboardEarlyUpgradeEligibleCard />
            </>
          )}

          <ImpactTracker />

          <DashboardAccountManagerCard accountManager="Jan Dzulko" />
        </Col>
      </Row>
    </Container>
  )
}
